import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import defaultImage from "../../Assets/images/image2.png";

const InterestMedia = ({ articles }) => {
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  const currentArticle = articles[index] || {};
  const isVideo = currentArticle.imageUrl?.endsWith(".mp4");

  const next = () => setIndex((prev) => (prev + 1) % articles.length);
  const prev = () => setIndex((prev) => (prev - 1 + articles.length) % articles.length);

  const toggleMute = () => {
    setMuted((prev) => !prev);
    if (videoRef.current) {
      videoRef.current.muted = !muted;
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      next();
    }, 7000);
    return () => clearInterval(timer);
  }, [index, articles.length]);

  return (
    <div style={styles.container}>
      {loading && (
        <div style={styles.loadingOverlay}>
          <div className="spinner"></div>
        </div>
      )}

      {/* Navigation Buttons */}
      {articles.length > 1 && (
        <>
          <button style={{ ...styles.navButton, left: "16px" }} onClick={prev}>
            &#8592;
          </button>
          <button style={{ ...styles.navButton, right: "16px" }} onClick={next}>
            &#8594;
          </button>
        </>
      )}

      {/* Media Slider */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentArticle.imageUrl}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5 }}
          style={styles.mediaContainer}
        >
          <div
            onClick={() => navigate(`/articles/${currentArticle.id}`)}
            style={styles.clickableArea}
          >
            {isVideo ? (
              <video
                ref={videoRef}
                src={`${process.env.REACT_APP_MEDIA_URL}${currentArticle.imageUrl}`}
                muted={muted}
                autoPlay
                loop
                playsInline
                style={styles.media}
              />
            ) : (
              <img
                src={
                  currentArticle.imageUrl
                    ? `${process.env.REACT_APP_MEDIA_URL}${currentArticle.imageUrl}`
                    : defaultImage
                }
                alt={currentArticle.title}
                style={styles.media}
              />
            )}
            {isVideo && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent navigation when toggling mute
                  toggleMute();
                }}
                style={styles.muteButton}
              >
                {muted ? "🔇" : "🔊"}
              </button>
            )}
            <div style={styles.caption}>
              <p style={styles.captionText}>{currentArticle.title}</p>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Pagination Dots */}
      {articles.length > 1 && (
        <div style={styles.pagination}>
          {articles.map((_, i) => (
            <div
              key={i}
              style={{
                ...styles.dot,
                backgroundColor: i === index ? "#007bff" : "#ccc",
              }}
            ></div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    position: "relative",
    height: "300px",
    borderRadius: "8px",
    overflow: "hidden",
  },
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.3)",
    zIndex: 1,
  },
  navButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    backgroundColor: "rgba(255,255,255,0.6)",
    border: "none",
    borderRadius: "50%",
    padding: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    transition: "background-color 0.3s",
  },
  mediaContainer: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
  },
  clickableArea: {
    position: "absolute",
    inset: 0,
    cursor: "pointer",
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  muteButton: {
    position: "absolute",
    bottom: "16px",
    right: "16px",
    backgroundColor: "rgba(0,0,0,0.6)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    padding: "8px",
    cursor: "pointer",
    fontSize: "16px",
  },
  caption: {
    position: "absolute",
    bottom: "16px",
    left: "16px",
    backgroundColor: "rgba(0,0,0,0.5)",
    borderRadius: "4px",
    padding: "8px",
    maxWidth: "85%",
  },
  captionText: {
    color: "#fff",
    fontWeight: "600",
    margin: 0,
  },
  pagination: {
    position: "absolute",
    bottom: "8px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: "8px",
    zIndex: 2,
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#ccc",
  },
};

export default InterestMedia;